import { ApiUrls, ContentType } from '../constants/constants';
import { CustomEventNames } from '../constants/custom-event-names';
import {
	type QuestionOpenData,
	type AnswerData,
	type QuestionaireData,
	type QuestionaireStartPageData,
	type ReportData,
	type ClarificationData,
} from '../interfaces/interfaces';

class ApiService {
	private readonly errorMessage: string = 'Er is iets fout gegaan, probeer het later opnieuw.';

	// get questionaire startpage data
	public async getQuestionnaire(): Promise<QuestionaireStartPageData> {
		return await this.getApiResponse(ApiUrls.Questionaire, 'GET', null);
	}

	// post to start questionnaire
	public async postStartQuestionnaire(): Promise<QuestionaireData> {
		return await this.getApiResponse(ApiUrls.QuestionnaireStart, 'POST', null);
	}

	// post to answer question
	public async postAnswerQuestion(answer: AnswerData): Promise<QuestionaireData> {
		return await this.getApiResponse(ApiUrls.QuestionAnswer, 'POST', answer);
	}

	// open question
	public async openQuestion(questionOpenData: QuestionOpenData): Promise<QuestionaireData> {
		return await this.getApiResponse(ApiUrls.QuestionOpen, 'POST', questionOpenData);
	}

	// open report
	public async openReport(responseCode: string): Promise<ReportData> {
		return await this.getApiResponse(ApiUrls.ReportOpen, 'POST', { responseCode });
	}

	// open questionaire
	public async openQuestionnaire(responseCode: string): Promise<QuestionaireData> {
		return await this.getApiResponse(ApiUrls.QuestionnaireOpen, 'POST', { responseCode });
	}

	// open clarification
	public async openClarification(clarificationId: string): Promise<ClarificationData> {
		return await this.getApiResponse(ApiUrls.ClarificationOpen, 'POST', { clarificationId });
	}

	private async getApiResponse<T>(apiUrl: string, method: string, body: object | null): Promise<T> {
		try {
			const parameters: RequestInit = {
				method,
				headers: {
					Accept: ContentType,
					'Content-Type': ContentType,
				},
			};

			if (body != null) {
				parameters.body = JSON.stringify(body);
			}

			const response = await fetch(`${apiUrl}`, parameters);

			if (
				response.status === 500 ||
				response.status === 400 ||
				response.status === 401 ||
				response.status === 403 ||
				response.status === 404
			) {
				const error = await response.json();
				throw new ApiException(response.status, error.detail);
			}

			if (!response.ok) {
				throw new ApiException(response.status, this.errorMessage);
			}

			if (response.headers.get('content-type')?.includes(ContentType) ?? false) {
				return (await response.json()) as T;
			}

			throw new ApiException(400, 'Response was not JSON');
		} catch (error) {
			if (!(error instanceof ApiException)) {
				this.triggerErrorDialog(-1, this.errorMessage);
			} else {
				this.triggerErrorDialog(error.statusCode, error.message);
			}
			throw error;
		}
	}

	private triggerErrorDialog(errorCode: number, errorMessage: string): void {
		const event = new CustomEvent(CustomEventNames.OpenErrorDialog, {
			detail: {
				message: { errorCode, errorMessage },
			},
		});
		window.dispatchEvent(event);
	}
}

export const apiService = new ApiService();

class ApiException extends Error {
	public statusCode: number;
	public handled: boolean = false;

	constructor(statusCode: number, message: string, handled: boolean = false) {
		super(message);
		Object.setPrototypeOf(this, ApiException.prototype);
		this.statusCode = statusCode;
		this.handled = handled;
	}
}
